// @flow

/**
 * In this file we configure all the routes used by the site.
 *
 * We configure the routes as objects with a toString() method that will return the route pattern.
 *
 * This allows us to add additional helper methods to each route to construct it from common parameter sets.
 *
 * If you need the actual route pattern, when defining routes, for example, simply cast the route as a string…
 *
 *     String(HOMEPAGE)
 *
 * If you find yourself trying to work out a route with parameters, then include a helper method on the route in
 * question for others to use in future.
 */

import invariant           from 'invariant';
import pathToRegexp        from 'path-to-regexp';
import React               from 'react';
import loadable            from '../components/HOC/loadable';
import resourceToImmutable from '../utils/resourceToImmutable';
import url                 from '../utils/url';
import Route               from './Route';

const PublicSite                    = loadable({
  loader: () => import('../components/sites/PublicSite'),
});
const Homepage                      = loadable({
  loader: () => import('../components/sites/PublicSite/pages/Homepage'),
});
const LogOutPage                    = loadable({
  loader: () => import('../components/sites/PublicSite/LogOutPage'),
});
const LoginPage                     = loadable({
  loader: () => import('../components/sites/PublicSite/LoginPage'),
});
const CookiesSettingsPage           = loadable({
  loader: () => import('../components/sites/PublicSite/pages/CookiesSettingsPage/CookiesSettingsPage'),
});
const EligibilityToolPage           = loadable({
  loader: () => import('../components/sites/PublicSite/pages/EligibilityToolPage'),
});
const CategoriesPage                = loadable({
  loader: () => import('../components/sites/PublicSite/pages/CategoriesPage'),
});
const BrowseServicesPage            = loadable({
  loader: () => import('../components/sites/PublicSite/pages/Services/BrowseServicesPage'),
});
const ServiceDetailsPage            = loadable({
  loader: () => import('../components/sites/PublicSite/pages/Services/ServiceDetailsPage'),
});
const CreateServiceProviderPage     = loadable({
  loader: () => import('../components/pages/public/ServiceProviders/CreateServiceProviderPage'),
});
const ServiceProviderDetailsPage    = loadable({
  loader: () => import('../components/sites/PublicSite/pages/ServiceProviders/ServiceProviderDetailsPage'),
});
const ShortlistPage                 = loadable({
  loader: () => import('../components/sites/PublicSite/pages/Shortlists/ShortlistPage'),
});
const ShortlistPrintPage            = loadable({
  loader: () => import('../components/sites/PublicSite/pages/Shortlists/ShortlistPrintPage'),
});
const Error404                      = loadable({
  loader: () => import('../components/pages/Error404'),
});
const AboutPage                     = loadable({
  loader: () => import('../components/sites/PublicSite/pages/AboutPage'),
});
const PrivacyPolicyPage             = loadable({
  loader: () => import('../components/sites/PublicSite/pages/PrivacyPolicyPage'),
});
const LocallyStoredPersonalDataPage = loadable({
  loader: () => import('../components/sites/PublicSite/pages/LocallyStoredPersonalDataPage'),
});
const Redirect                      = loadable({
  loader:      () => import('react-router-dom'),
  namedExport: 'Redirect',
});
const AdminSite                     = loadable({
  loader: () => import('../components/sites/AdminSite'),
});
const ServiceProvidersListPage      = loadable({
  loader: () => import('../components/resources/ServiceProvider/ServiceProvidersListPage'),
});

// Preload all the routes after a few seconds page has loaded
// @todo Update this preload to happen on the actual rendered component's `componentDidMount()` lifecycle event.
setTimeout(() => {
  PublicSite.preload();
  Homepage.preload();
  LogOutPage.preload();
  LoginPage.preload();
  CookiesSettingsPage.preload();
  EligibilityToolPage.preload();
  CategoriesPage.preload();
  BrowseServicesPage.preload();
  ServiceDetailsPage.preload();
  CreateServiceProviderPage.preload();
  ServiceProviderDetailsPage.preload();
  ShortlistPage.preload();
  ShortlistPrintPage.preload();
  Error404.preload();
  AboutPage.preload();
  PrivacyPolicyPage.preload();
  LocallyStoredPersonalDataPage.preload();
  Redirect.preload();
  AdminSite.preload();
  ServiceProvidersListPage.preload();
}, 3000);

/**
 * To add a route to the config,
 */
class RouteConfig {
  PUBLIC_SITE: Route;
  HOMEPAGE: Route;
  LOGIN: Route;
  LOGOUT: Route;
  COOKIES_SETTINGS: Route;
  ELIGIBILITY_TOOL:
    & Route
    & { intro: () => string }
    & { go: () => string };
  CATEGORIES: Route;
  SERVICES_LIST:
    & Route
    & { forView: () => string }
    & { forTaxonomyTerm: ({ term: Map<string, *>, view: 'list' | 'map' }) => string, };
  SERVICE_DETAILS: Route & { forService: (service: Map<string, *>) => string };
  SERVICE_PROVIDERS_CREATE: Route;
  SERVICE_PROVIDER_DETAILS: Route;
  SHORTLIST: Route;
  PRINT_SHORTLIST: Route;
  ABOUT_US: Route;
  PRIVACY_POLICY: Route;
  LOCALLY_STORED_PERSONAL_DATA: Route;
  ERROR_404: Route;
  NOT_FOUND: Route;
  ADMIN_SITE: Route;
  ADMIN_SERVICE_PROVIDERS_LIST: Route;
  ADMIN_NOT_FOUND: Route;

  constructor() {
    this.PUBLIC_SITE = new Route({
      name:      'PUBLIC_SITE',
      component: PublicSite,
    });

    this.HOMEPAGE = new Route({
      name:         'HOMEPAGE',
      parent:       this.PUBLIC_SITE,
      relativePath: '/',
      exact:        true,
      component:    Homepage,
    });

    // -----------------------------------------------------------------------------------------------------------------
    // User account pages
    // -----------------------------------------------------------------------------------------------------------------

    // This route exists to allow future expansion for registerin for different types of accounts in the future.
    this.LOGIN = new Route({
      name:         'LOGIN',
      parent:       this.PUBLIC_SITE,
      relativePath: '/login',
      exact:        false,
      component:    LoginPage,
    });

    // This route exists to allow future expansion for registerin for different types of accounts in the future.
    this.LOGOUT = new Route({
      name:         'LOGOUT',
      parent:       this.PUBLIC_SITE,
      relativePath: '/logout',
      exact:        true,
      component:    LogOutPage,
    });

    // -----------------------------------------------------------------------------------------------------------------
    // Settings
    // -----------------------------------------------------------------------------------------------------------------

    this.COOKIES_SETTINGS = new Route({
      name:         'COOKIES_SETTINGS',
      parent:       this.PUBLIC_SITE,
      relativePath: '/cookies',
      exact:        true,
      component:    CookiesSettingsPage,
    });

    // -----------------------------------------------------------------------------------------------------------------
    // Tools
    // -----------------------------------------------------------------------------------------------------------------

    this.ELIGIBILITY_TOOL = new Route({
      name:         'ELIGIBILITY_TOOL',
      parent:       this.PUBLIC_SITE,
      relativePath: '/eligibility-tool/:go(go)?',
      exact:        true,
      component:    EligibilityToolPage,
    });

    this.ELIGIBILITY_TOOL.intro = () => pathToRegexp.compile(this.ELIGIBILITY_TOOL.fullPath)();

    this.ELIGIBILITY_TOOL.go = () => pathToRegexp.compile(this.ELIGIBILITY_TOOL.fullPath)({go: 'go'});

    // -----------------------------------------------------------------------------------------------------------------
    // Service Categories
    // -----------------------------------------------------------------------------------------------------------------

    this.CATEGORIES = new Route({
      name:         'CATEGORIES',
      parent:       this.PUBLIC_SITE,
      relativePath: '/categories/:selected?',
      exact:        true,
      component:    CategoriesPage,
    });

    // -----------------------------------------------------------------------------------------------------------------
    // Services
    // -----------------------------------------------------------------------------------------------------------------

    this.SERVICES_LIST = new Route({
      name:         'SERVICES_LIST',
      parent:       this.PUBLIC_SITE,
      relativePath: '/services/:view(list|map)',
      exact:        true,
      component:    BrowseServicesPage,
    });

    this.SERVICES_LIST.forView = ({view = 'list', filters = []} = {}) =>
      pathToRegexp.compile(this.SERVICES_LIST.fullPath)({view, filters});

    this.SERVICES_LIST.forTaxonomyTerm = ({term, view = 'list'}) => {
      term               = resourceToImmutable(term);
      const taxonomySlug = term.getIn(['taxonomy', 'slug'], '');
      invariant(taxonomySlug, 'Missing taxonomySlug.  You might need to inject it into the term.');
      return url.addQueryParams(pathToRegexp.compile(this.SERVICES_LIST.fullPath)({view}), {
        [taxonomySlug]: [
          term.get('slug', '')
            .replace(new RegExp(`^${taxonomySlug}-`), ''),
        ],
      });
    };

    this.SERVICE_DETAILS = new Route({
      name:         'SERVICE_DETAILS',
      parent:       this.PUBLIC_SITE,
      relativePath: '/services/view/:resourceSlug',
      component:    ServiceDetailsPage,
    });

    this.SERVICE_DETAILS.forService = service => {
      service = resourceToImmutable(service);
      return pathToRegexp.compile(this.SERVICE_DETAILS.fullPath)({
        resourceSlug: service.get('slug'),
      });
    };

    // -----------------------------------------------------------------------------------------------------------------
    // Service Providers
    // -----------------------------------------------------------------------------------------------------------------

    this.SERVICE_PROVIDERS_CREATE = new Route({
      name:         'SERVICE_PROVIDERS_CREATE',
      parent:       this.PUBLIC_SITE,
      relativePath: '/service-providers/create',
      component:    CreateServiceProviderPage,
    });

    this.SERVICE_PROVIDER_DETAILS = new Route({
      name:         'SERVICE_PROVIDER_DETAILS',
      parent:       this.PUBLIC_SITE,
      relativePath: '/service-providers/:slug',
      component:    ServiceProviderDetailsPage,
    });

    this.SERVICE_PROVIDER_DETAILS.forServiceProvider = serviceProvider =>
      pathToRegexp.compile(this.SERVICE_PROVIDER_DETAILS.fullPath)({
        slug: serviceProvider.slug,
      });

    // -----------------------------------------------------------------------------------------------------------------
    // Shortlists
    // -----------------------------------------------------------------------------------------------------------------

    this.SHORTLIST = new Route({
      name:         'SHORTLIST',
      parent:       this.PUBLIC_SITE,
      relativePath: '/shortlists/:slug/:view(list|map)',
      exact:        true,
      component:    ShortlistPage,
    });

    this.SHORTLIST.forLocalShortlistWithView = ({view = 'list'} = {}) =>
      pathToRegexp.compile(this.SHORTLIST.fullPath)({slug: 'local', view});

    this.PRINT_SHORTLIST = new Route({
      name:         'SHORTLIST',
      parent:       this.PUBLIC_SITE,
      relativePath: '/shortlists/:slug/print',
      exact:        true,
      component:    ShortlistPrintPage,
    });

    this.PRINT_SHORTLIST.forLocalShortlist = () =>
      pathToRegexp.compile(this.PRINT_SHORTLIST.fullPath)({slug: 'local'});

    this.ABOUT_US = new Route({
      name:         'ABOUT_US',
      parent:       this.PUBLIC_SITE,
      relativePath: '/about-the-ilcp',
      exact:        true,
      component:    AboutPage,
    });

    /**
     * IMPORTANT NOTE
     * ==============
     *
     * DO NOT REMOVE THIS ROUTE FOR ANY REASON!
     *
     * @type {Route}
     */
    this.PRIVACY_POLICY = new Route({
      name:         'PRIVACY_POLICY',
      parent:       this.PUBLIC_SITE,
      relativePath: '/privacy-policy',
      exact:        true,
      component:    PrivacyPolicyPage,
    });

    this.LOCALLY_STORED_PERSONAL_DATA = new Route({
      name:         'PERSONAL_DATA',
      parent:       this.PUBLIC_SITE,
      relativePath: '/your-personal-data',
      exact:        true,
      component:    LocallyStoredPersonalDataPage,
    });

    this.ERROR_404 = new Route({
      name:         'ERROR_404',
      parent:       this.PUBLIC_SITE,
      relativePath: '/404',
      exact:        true,
      component:    Error404,
    });

    this.NOT_FOUND = new Route({
      name:      'NOT_FOUND',
      parent:    this.PUBLIC_SITE,
      component: Redirect,
      props:     {to: this.ERROR_404.fullPath},
    });

    // -----------------------------------------------------------------------------------------------------------------
    // Admin routes
    // -----------------------------------------------------------------------------------------------------------------

    this.ADMIN_SITE = new Route({
      name:         'ADMIN_SITE',
      relativePath: '/admin',
      component:    AdminSite,
    });

    this.ADMIN_SERVICE_PROVIDERS_LIST = new Route({
      name:         'ADMIN_SERVICE_PROVIDERS_LIST',
      parent:       this.ADMIN_SITE,
      relativePath: '/service-providers/list',
      component:    ServiceProvidersListPage,
      exact:        true,
    });

    this.ADMIN_NOT_FOUND = new Route({
      name:      'ADMIN_NOT_FOUND',
      parent:    this.ADMIN_SITE,
      component: ({location}) => (
        <Redirect
          to={url.addQueryParams(this.ERROR_404.fullPath, {
            from: location.pathname,
          })}
        />
      ),
    });
  }

  getSites() {
    return [this.ADMIN_SITE, this.PUBLIC_SITE];
  }
}

const routes = new RouteConfig();

export default routes;



// WEBPACK FOOTER //
// ./src/config/routes.js